import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, CheckCircle, XCircle, Clock, Search, 
  Mail, Phone, Building2, DollarSign, FileText, RefreshCw,
  Filter, AlertCircle, Shield, Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvestorApplications = () => {
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchApplications();
  }, [user, filter, authLoading]);

  const fetchApplications = async () => {
    try {
      const url = filter === 'all' 
        ? `${API}/admin/investor-applications`
        : `${API}/admin/investor-applications?status_filter=${filter}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setApplications(data.applications || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (applicationId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/investor-applications/${applicationId}/approve`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Application approved! Investor can now login.');
        setSelectedApp(null);
        fetchApplications();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Error approving application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (applicationId) => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/admin/investor-applications/${applicationId}/reject?reason=${encodeURIComponent(rejectReason)}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Application rejected');
        setSelectedApp(null);
        setRejectReason('');
        fetchApplications();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Error rejecting application');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (applicationId) => {
    if (!confirm('Are you sure you want to delete this application?')) return;
    
    try {
      const res = await fetch(`${API}/admin/investor-applications/${applicationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Application deleted');
        setSelectedApp(null);
        fetchApplications();
      }
    } catch (error) {
      toast.error('Error deleting application');
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      app.name?.toLowerCase().includes(search) ||
      app.email?.toLowerCase().includes(search) ||
      app.company?.toLowerCase().includes(search) ||
      app.application_id?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 rounded text-xs bg-yellow-500/20 text-yellow-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>;
      default:
        return <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">{status}</span>;
    }
  };

  const getVerificationLabel = (method) => {
    const labels = {
      'income': 'Income Verification',
      'net_worth': 'Net Worth',
      'professional': 'Professional',
      'entity': 'Entity/Trust',
      'entity_assets': 'Entity Assets (>$5M)',
      'beneficial_owners': 'Beneficial Owners',
      'qualified_purchaser': 'Qualified Purchaser',
      'institutional': 'Institutional Investor'
    };
    return labels[method] || method;
  };

  const getEntityTypeLabel = (type) => {
    const labels = {
      'individual': 'Individual',
      'trust': 'Trust',
      'spv_llc': 'SPV / LLC',
      'family_office': 'Family Office',
      'private_fund': 'Private Fund'
    };
    return labels[type] || type;
  };

  const getParticipantRoleLabel = (role) => {
    const labels = {
      'investor': 'Direct Investor',
      'private_banker': 'Private Banker',
      'introducer': 'Introducer',
      'family_office_manager': 'Family Office Manager',
      'facilitator': 'Facilitator'
    };
    return labels[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'investor': 'bg-blue-500/20 text-blue-400',
      'private_banker': 'bg-purple-500/20 text-purple-400',
      'introducer': 'bg-orange-500/20 text-orange-400',
      'family_office_manager': 'bg-emerald-500/20 text-emerald-400',
      'facilitator': 'bg-pink-500/20 text-pink-400'
    };
    return colors[role] || 'bg-gray-500/20 text-gray-400';
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#b8860b] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="investor-applications-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin-dashboard')}
              className="text-gray-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#b8860b] flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold">Investor Applications</h1>
                <p className="text-gray-400 text-xs">Review and approve accredited investor applications</p>
              </div>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchApplications}
            className="border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Total Applications</p>
                  <p className="text-2xl font-bold text-white">{applications.length}</p>
                </div>
                <FileText className="w-8 h-8 text-[#b8860b] opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Pending Review</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Approved</p>
                  <p className="text-2xl font-bold text-green-400">{stats.approved}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-xs">Rejected</p>
                  <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Search by name, email, company, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1a2d4a] border-[#2a3d5a] text-white"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(status)}
                className={filter === status 
                  ? 'bg-[#b8860b] text-white' 
                  : 'border-[#2a3d5a] text-gray-400 hover:bg-[#1a2d4a]'}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Applications List */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {loading ? (
            <div className="col-span-2 text-center py-12">
              <RefreshCw className="w-8 h-8 text-[#b8860b] animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading applications...</p>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="col-span-2 text-center py-12">
              <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No applications found</p>
            </div>
          ) : (
            filteredApplications.map((app) => (
              <Card 
                key={app.application_id}
                className={`bg-[#0d1d35] border-[#1a2d4a] cursor-pointer transition-colors hover:border-[#b8860b]/50 ${
                  selectedApp?.application_id === app.application_id ? 'ring-2 ring-[#b8860b]' : ''
                }`}
                onClick={() => setSelectedApp(app)}
                data-testid={`application-card-${app.application_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white font-medium">{app.name}</p>
                      <p className="text-gray-500 text-xs font-mono">{app.application_id}</p>
                    </div>
                    {getStatusBadge(app.status)}
                  </div>

                  {/* Entity Type & Role Badges */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="px-2 py-0.5 rounded text-xs bg-[#b8860b]/20 text-[#b8860b]">
                      {getEntityTypeLabel(app.entity_type)}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getRoleBadgeColor(app.participant_role)}`}>
                      {getParticipantRoleLabel(app.participant_role)}
                    </span>
                    {app.broker_dealer_exempt && (
                      <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
                        BD Exempt
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Mail className="w-3 h-3" />
                      <span>{app.email}</span>
                    </div>
                    {app.phone && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Phone className="w-3 h-3" />
                        <span>{app.phone}</span>
                      </div>
                    )}
                    {app.company && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <Building2 className="w-3 h-3" />
                        <span>{app.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-400">
                      <Shield className="w-3 h-3" />
                      <span>{getVerificationLabel(app.verification_method)}</span>
                    </div>
                    {app.investment_range && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <DollarSign className="w-3 h-3" />
                        <span>{app.investment_range}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-[#1a2d4a]">
                    <p className="text-gray-500 text-xs">
                      Submitted: {new Date(app.submitted_at).toLocaleDateString()} at {new Date(app.submitted_at).toLocaleTimeString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Selected Application Detail Modal */}
        {selectedApp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedApp(null)}>
            <Card 
              className="bg-[#0d1d35] border-[#1a2d4a] max-w-lg w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="application-detail-modal"
            >
              <CardHeader className="border-b border-[#1a2d4a]">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">{selectedApp.name}</CardTitle>
                    <p className="text-gray-500 text-xs font-mono">{selectedApp.application_id}</p>
                  </div>
                  {getStatusBadge(selectedApp.status)}
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Entity Type & Role */}
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded text-sm bg-[#b8860b]/20 text-[#b8860b] font-medium">
                    {getEntityTypeLabel(selectedApp.entity_type)}
                  </span>
                  <span className={`px-3 py-1 rounded text-sm font-medium ${getRoleBadgeColor(selectedApp.participant_role)}`}>
                    {getParticipantRoleLabel(selectedApp.participant_role)}
                  </span>
                  {selectedApp.broker_dealer_exempt && (
                    <span className="px-3 py-1 rounded text-sm bg-blue-500/20 text-blue-400 font-medium">
                      BD Exemption Acknowledged
                    </span>
                  )}
                </div>

                {/* Contact Info */}
                <div className="space-y-2">
                  <h3 className="text-white font-semibold text-sm">Contact Information</h3>
                  <div className="bg-[#1a2d4a] rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Mail className="w-4 h-4 text-[#b8860b]" />
                      <span>{selectedApp.email}</span>
                    </div>
                    {selectedApp.phone && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone className="w-4 h-4 text-[#b8860b]" />
                        <span>{selectedApp.phone}</span>
                      </div>
                    )}
                    {selectedApp.company && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Building2 className="w-4 h-4 text-[#b8860b]" />
                        <span>{selectedApp.company}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Accreditation Details */}
                <div className="space-y-2">
                  <h3 className="text-white font-semibold text-sm">Accreditation Details</h3>
                  <div className="bg-[#1a2d4a] rounded-lg p-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Entity Type</span>
                      <span className="text-white text-sm">{getEntityTypeLabel(selectedApp.entity_type)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Participant Role</span>
                      <span className="text-white text-sm">{getParticipantRoleLabel(selectedApp.participant_role)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Verification Method</span>
                      <span className="text-white text-sm">{getVerificationLabel(selectedApp.verification_method)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Investment Range</span>
                      <span className="text-white text-sm">{selectedApp.investment_range || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">Self Certified</span>
                      <span className="text-green-400 text-sm">{selectedApp.self_certification ? 'Yes' : 'No'}</span>
                    </div>
                    {selectedApp.broker_dealer_exempt && (
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">BD Exemption</span>
                        <span className="text-blue-400 text-sm">Acknowledged</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Message */}
                {selectedApp.message && (
                  <div className="space-y-2">
                    <h3 className="text-white font-semibold text-sm">Additional Message</h3>
                    <div className="bg-[#1a2d4a] rounded-lg p-3">
                      <p className="text-gray-300 text-sm">{selectedApp.message}</p>
                    </div>
                  </div>
                )}

                {/* Rejection Reason (if rejected) */}
                {selectedApp.status === 'rejected' && selectedApp.rejection_reason && (
                  <div className="space-y-2">
                    <h3 className="text-red-400 font-semibold text-sm">Rejection Reason</h3>
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      <p className="text-red-300 text-sm">{selectedApp.rejection_reason}</p>
                    </div>
                  </div>
                )}

                {/* Actions for Pending Applications */}
                {selectedApp.status === 'pending' && (
                  <div className="space-y-4 pt-4 border-t border-[#1a2d4a]">
                    {/* Reject Reason Input */}
                    <div className="space-y-2">
                      <label className="text-gray-400 text-sm">Rejection Reason (required for rejection)</label>
                      <Input
                        placeholder="Reason for rejection..."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                        data-testid="reject-reason-input"
                      />
                    </div>
                    
                    <div className="flex gap-3">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleApprove(selectedApp.application_id)}
                        disabled={actionLoading}
                        data-testid="approve-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {actionLoading ? 'Processing...' : 'Approve'}
                      </Button>
                      <Button
                        className="flex-1 bg-red-600 hover:bg-red-700"
                        onClick={() => handleReject(selectedApp.application_id)}
                        disabled={actionLoading || !rejectReason.trim()}
                        data-testid="reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        {actionLoading ? 'Processing...' : 'Reject'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Delete Option */}
                <div className="pt-4 border-t border-[#1a2d4a]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    onClick={() => handleDelete(selectedApp.application_id)}
                    data-testid="delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Application
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorApplications;
